import React from "react";
import { Link } from "react-router-dom";
import { Clock, Target, TrendingUp, ArrowRight } from "lucide-react";
import OpportunityBadge from "@/components/OpportunityBadge";
import ScoreBadge from "@/components/ScoreBadge";
import SignalActionBar from "@/components/SignalActionBar";
import { timeAgo, fmtPct } from "@/lib/format";

const CONF_STYLE = {
  High:   "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  Medium: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  Low:    "text-zinc-400 border-zinc-500/40 bg-zinc-500/10",
};

/**
 * Investigation Queue card. Every field on the v2.5A card contract:
 *   Opportunity Rating · Signal Score · Confidence · Remaining Edge ·
 *   Why this matters · Estimated research time · Investigate CTA
 */
export default function InvestigationCard({ card, rank }) {
  const edgePct = card.remaining_edge_pct != null
    ? Math.round(card.remaining_edge_pct * 100)
    : null;

  return (
    <article
      className="border border-zinc-800 rounded-md p-4 hover:border-zinc-600 hover:bg-zinc-900/40 transition-all"
      data-testid={`investigation-card-${card.signal_id}`}
    >
      <div className="flex items-start gap-4">
        {/* Rank */}
        <div className="shrink-0 w-8 text-center">
          <div className="text-2xl font-heading font-bold text-zinc-500 leading-none">
            {String(rank).padStart(2, "0")}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          {/* Title + badges */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Link
                to={`/app/signals/${card.signal_id}`}
                className="block font-heading text-base md:text-lg font-semibold text-zinc-100 hover:underline leading-snug"
                data-testid={`investigation-title-${card.signal_id}`}
              >
                {card.market_title}
              </Link>
              <div className="mt-1 text-[11px] text-zinc-500 flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>{card.category || "Uncategorized"}</span>
                <span>·</span>
                <span>direction {card.outcome}</span>
                <span>·</span>
                <span>{card.n_participating_traders} traders</span>
                <span>·</span>
                <span>{timeAgo(card.detected_at)}</span>
              </div>
            </div>
            <div className="shrink-0 flex items-center gap-1.5">
              <OpportunityBadge rating={card.opportunity_rating}
                                testid={`investigation-rating-${card.signal_id}`} />
              <ScoreBadge score={card.signal_score}
                          testid={`investigation-score-${card.signal_id}`} />
            </div>
          </div>

          {/* Why this matters */}
          <p className="mt-2 text-sm text-zinc-300 leading-snug"
             data-testid={`investigation-why-${card.signal_id}`}>
            {card.why_this_matters}
          </p>

          {/* Metric strip */}
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
            <MiniStat icon={Target}
                      label="Confidence"
                      value={card.confidence}
                      tip="Confidence in this signal — independent of the score. High = many quality traders + complete data + strong concentration."
                      className={CONF_STYLE[card.confidence] || CONF_STYLE.Low} />
            <MiniStat icon={TrendingUp}
                      label="Remaining edge"
                      tip="Percentage of the original entry-to-target price gap still open. 100% = full edge remains; 0% = price already moved past the traders' entry."
                      value={edgePct != null ? `${edgePct}%` : "—"} />
            <MiniStat icon={Clock}
                      label="Research time"
                      tip="Estimated minutes to deep-dive this opportunity: reviewing traders, dissenters, and category context."
                      value={`~${card.estimated_research_minutes} min`} />
            <MiniStat icon={TrendingUp}
                      label="Current / entry"
                      tip="Current market probability / traders' estimated average entry. Larger gap = more edge left on the table."
                      value={
                        card.current_probability != null && card.estimated_average_entry != null
                          ? `${fmtPct(card.current_probability, 0)} / ${fmtPct(card.estimated_average_entry, 0)}`
                          : "—"
                      } />
          </div>

          {/* CTA + actions */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <SignalActionBar
              signalId={card.signal_id}
              currentAction={card.user_action}
              size="sm"
              testid={`investigation-actions-${card.signal_id}`}
            />
            <Link
              to={`/app/signals/${card.signal_id}`}
              data-testid={`investigation-cta-${card.signal_id}`}
              className="group inline-flex items-center gap-1 text-sm font-heading font-semibold text-zinc-100 border border-zinc-700 hover:border-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 rounded-md px-3 py-1.5 transition-all"
            >
              Investigate
              <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

function MiniStat({ icon: Icon, label, value, tip, className = "" }) {
  return (
    <div className={`rounded-sm border border-zinc-800 bg-zinc-900/40 px-2 py-1.5 ${className}`}
         title={tip}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
        <Icon size={9} />
        {label}
      </div>
      <div className="text-sm font-heading font-semibold mt-0.5">{value}</div>
    </div>
  );
}
